import React from "react";
import dayjs from "dayjs";
import {
  Form,
  Input,
  InputNumber,
  Select,
  DatePicker,
  Radio,
  Checkbox,
  Button,
} from "antd";
import Dragger from "antd/es/upload/Dragger";
import TableWithSkeleton from "../../../../components/table/TableWithSkeleton.jsx";
import {
  AlternativeMobileNumberValidator,
  DocumentUploadButton,
  mobileNumberValidator,
} from "../../../../utils/utils.js";
import AlternateTaxIdentifierField from "../../../../components/onboarding/AlternateTaxIdentifierField.jsx";

const { TextArea } = Input;

const IndividualBeneficiaryForm = ({
  countries,
  countryCodes,
  tinReasonOptions,
  proofTypeOptions,
  previewUrl,
  hasOtherCrsTaxResident,
  isUSPerson,
  acceptDeclaration,
  isPEP,
  documentFileList,
  proofType,
  documentSubType,
  uploadedDocuments,
  documentColumns,
  filterOption,
  handleUSPersonChange,
  setAcceptDeclaration,
  handlePEPChange,
  handleProofTypeChange,
  handleDocumentSubTypeChange,
  handleDocumentChange,
  handleAddDocument,
  handleRemoveDocument,
  handleMultiTaxResidentChange,
  isDisabled,
}) => {
  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-4">
          <Form.Item
            label="First Name"
            name="firstName"
            rules={[{ required: true, message: "Please enter first name" }]}
          >
            <Input placeholder="Enter first name" />
          </Form.Item>

          <Form.Item
            label="Date Of Birth"
            name="dateOfBirth"
            rules={[{ required: true, message: "Please select date of birth" }]}
          >
            <DatePicker
              format="DD/MM/YYYY"
              placeholder="DD/MM/YYYY"
              className="w-full"
              style={{ width: "100%" }}
              disabledDate={(current) =>
                current && current > dayjs().endOf("day")
              }
            />
          </Form.Item>

          <Form.Item label="Mobile Number *" required>
            <div className="flex w-full gap-2">
              <Form.Item
                name="mobileCountryCode"
                noStyle
                rules={[{ required: true, message: "Country code required" }]}
              >
                <Select
                  showSearch
                  placeholder="Code"
                  filterOption={filterOption}
                  options={countryCodes}
                  style={{ width: "30%" }}
                />
              </Form.Item>
              <Form.Item
                name="mobileNumber"
                noStyle
                dependencies={["mobileCountryCode"]}
                rules={[mobileNumberValidator()]}
              >
                <Input
                  placeholder="Enter mobile number"
                  maxLength={15}
                  inputMode="numeric"
                  onKeyPress={(e) => {
                    if (!/[0-9]/.test(e.key)) {
                      e.preventDefault();
                    }
                  }}
                />
              </Form.Item>
            </div>
          </Form.Item>

          {/* Nationality */}
          <Form.Item
            label="Nationality"
            name="nationality"
            rules={[{ required: true, message: "Please select nationality" }]}
          >
            <Select
              showSearch
              placeholder="Select nationality"
              filterOption={filterOption}
              options={countries}
            />
          </Form.Item>

          {/* Country of residence */}
          <Form.Item
            label="Country of residence"
            name="countryOfResidence"
            rules={[
              { required: true, message: "Please select country of residence" },
            ]}
          >
            <Select
              showSearch
              placeholder="Select country of residence"
              filterOption={filterOption}
              options={countries}
            />
          </Form.Item>
        </div>

        {/* Right Column */}
        <div className="space-y-4">
          {/* Last Name */}
          <Form.Item
            label="Last Name"
            name="lastName"
            rules={[{ required: true, message: "Please enter last name" }]}
          >
            <Input placeholder="Enter last name" />
          </Form.Item>

          {/* Email */}
          <Form.Item
            label="Email"
            name="email"
            rules={[
              { required: true, message: "Please enter email" },
              { type: "email", message: "Please enter a valid email" },
            ]}
          >
            <Input type="email" placeholder="Enter email" />
          </Form.Item>

          {/* Alternative Mobile Number */}
          <Form.Item label="Alternative Mobile Number">
            <div className="flex w-full gap-2">
              <Form.Item name="alternativeMobileCountryCode" noStyle>
                <Select
                  showSearch
                  placeholder="Code"
                  filterOption={filterOption}
                  options={countryCodes}
                  style={{ width: "30%" }}
                />
              </Form.Item>
              <Form.Item
                name="alternativeMobileNumber"
                noStyle
                dependencies={["alternativeMobileCountryCode"]}
                rules={[AlternativeMobileNumberValidator()]}
                className="flex-1 min-w-0"
              >
                <Input
                  placeholder="Enter alternative mobile number"
                  maxLength={15}
                  inputMode="numeric"
                  onKeyPress={(e) => {
                    if (!/[0-9]/.test(e.key)) {
                      e.preventDefault();
                    }
                  }}
                />
              </Form.Item>
            </div>
          </Form.Item>

          {/* Country of Birth */}
          <Form.Item
            label="Country of Birth"
            name="countryOfBirth"
            rules={[
              { required: true, message: "Please select country of birth" },
            ]}
          >
            <Select
              showSearch
              placeholder="Select country of birth"
              filterOption={filterOption}
              options={countries}
            />
          </Form.Item>

          {/* Country of Tax Residence */}
          <Form.Item
            label="Country of Tax Residence"
            name="countryOfTaxResidence"
            rules={[
              {
                required: true,
                message: "Please select country of tax residence",
              },
            ]}
          >
            <Select
              showSearch
              placeholder="Select country of tax residence"
              filterOption={filterOption}
              options={countries}
            />
          </Form.Item>
        </div>
        <Form.Item
          label="Ownership Percentage"
          name="ownershipPercentage"
          rules={[
            { required: true, message: "Please enter ownership percentage" },
            {
              type: "number",
              min: 0,
              max: 100,
              message: "Value must be between 0 and 100",
            },
          ]}
        >
          <InputNumber
            min={0}
            max={100}
            placeholder="0"
            style={{ width: "100%" }}
          />
        </Form.Item>
      </div>

      {/* Registered Address Section */}
      <div className="mt-6 pt-6 border-t border-gray-200">
        <h3 className="text-lg font-semibold mb-4">Registered address</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Left Column */}
          <div className="space-y-4">
            {/* Apartment/Villa Number/Name */}
            <Form.Item
              label="Apartment/Villa Number/Name"
              name="apartmentVillaNumber"
              rules={[
                {
                  required: true,
                  message: "Please enter apartment/villa number/name",
                },
              ]}
            >
              <Input placeholder="Enter apartment/villa number/name" />
            </Form.Item>

            {/* Country */}
            <Form.Item
              label="Country"
              name="registeredAddressCountry"
              rules={[{ required: true, message: "Please select country" }]}
            >
              <Select
                showSearch
                placeholder="Select country"
                filterOption={filterOption}
                options={countries}
              />
            </Form.Item>

            {/* Postal/Zip Code */}
            <Form.Item
              label="Postal/Zip Code"
              name="postalZipCode"
              rules={[
                { required: true, message: "Please enter postal/zip code" },
              ]}
            >
              <Input placeholder="Enter postal/zip code" />
            </Form.Item>
          </div>

          {/* Right Column */}
          <div className="space-y-4">
            {/* Street Name/Area */}
            <Form.Item
              label="Street Name/Area"
              name="streetNameArea"
              rules={[
                { required: true, message: "Please enter street name/area" },
              ]}
            >
              <Input placeholder="Enter street name/area" />
            </Form.Item>

            {/* City / Town */}
            <Form.Item
              label="City / Town"
              name="cityTown"
              rules={[{ required: true, message: "Please enter city/town" }]}
            >
              <Input placeholder="Enter city/town" />
            </Form.Item>
          </div>
        </div>
      </div>

      {/* Tax Information Section */}
      <div className="mt-6 pt-6 border-t border-gray-200">
        <h3 className="text-lg font-semibold mb-4">Tax Information</h3>

        {/* FATCA and CRS Section */}
        <div className="mb-6">
          <h4 className="text-base font-semibold mb-2">
            Are you a US Resident, Citizen, or US tax payer as defined by the
            Foreign Account Tax Compliance Act 2010? *
          </h4>
          <p className="text-sm text-gray-600 mb-4">
            The term US Person means a US citizen or resident individual, a
            partnership or corporation organized in the United States or under
            the laws of the United States or any other State thereof, a trust if
            (i) a court within the United States would have authority under
            applicable law to render orders or judgments concerning
            substantially all issues regarding administration of the trust, and
            (ii) one or more US persons have the authority to control all
            substantial decisions of the trust, or an estate of a decedent that
            is a citizen or resident of the United States. This subparagraph
            shall be interpreted in accordance with the US Internal Revenue
            Code. If YES, please provide a W-9 form and your Tax Payer
            Identification Number (US TIN).
          </p>

          {/* Are you a U.S. Person? */}
          <Form.Item
            name="isUSPerson"
            rules={[{ required: true, message: "Please select an option" }]}
          >
            <Radio.Group onChange={handleUSPersonChange}>
              <Radio value={true}>Yes</Radio>
              <Radio value={false}>No</Radio>
            </Radio.Group>
          </Form.Item>

          {isUSPerson !== null && (
            <div className="mt-4">
              <p className="text-sm text-gray-600 mb-4">
                Please note this also includes if you are a US green Card
                holder, hold a US bank account, have a US PO box or postal
                address, have a US based power of attorney etc. Please visit the
                website www.irs.gov for full details or consult a foreign tax
                advisor.
              </p>

              {/* Declaration Box */}
              <div className="border border-gray-300 rounded-lg p-4 bg-gray-50 mb-4">
                <p className="text-sm text-gray-700 mb-3">
                  {isUSPerson
                    ? "I declare that I AM a U.S. Person and am subject to U.S. federal income taxes. I, hereby represent that, and by checking the applicable box below, the information provided by me is true and accurate."
                    : "I declare that I AM not a U.S. Person and am not subject to U.S. federal income taxes. I, hereby represent that, and by checking the applicable box below, the information provided by me is true and accurate."}
                </p>
                <Form.Item
                  name="acceptDeclaration"
                  valuePropName="checked"
                  rules={[
                    {
                      validator: (_, value) =>
                        value
                          ? Promise.resolve()
                          : Promise.reject("Please accept the declaration"),
                    },
                  ]}
                >
                  <Checkbox
                    checked={acceptDeclaration}
                    onChange={(e) => setAcceptDeclaration(e.target.checked)}
                  >
                    I accept above declaration
                  </Checkbox>
                </Form.Item>
              </div>

              {/* Social Security Number (only for US Person) */}
              {isUSPerson && (
                <Form.Item
                  label="Social Security Number"
                  name="socialSecurityNumber"
                  rules={[
                    {
                      required: true,
                      message: "Please enter social security number",
                    },
                    {
                      min: 7,
                      message:
                        "Social Security Number must be at least 7 characters",
                    },
                  ]}
                >
                  <Input placeholder="Enter social security number" />
                </Form.Item>
              )}
            </div>
          )}
        </div>

        {/* Common Reporting Standard (CRS) Section */}
        <div className="mt-6">
          <Form.Item
            label="For Common Reporting Standard (CRS) purposes, please mention which countries you are tax resident of?"
            name="crsTaxResident"
            // rules={[{ required: true, message: 'Please provide CRS tax residency information' }]}
          >
            <div></div>
          </Form.Item>

          <div className="border border-gray-300 rounded-lg p-6 mt-6">
            <div className="mb-6">
              <Form.Item
                label="Are there any other countries you are tax resident of?"
                name="isMultiTaxDomicileEnabled"
                rules={[{ required: true, message: "Please select an option" }]}
              >
                <Radio.Group onChange={handleMultiTaxResidentChange}>
                  <Radio value={true}>Yes</Radio>
                  <Radio value={false}>No</Radio>
                </Radio.Group>
              </Form.Item>
            </div>

            <Form.List name="taxDomiciles">
              {(fields, { add, remove }) => (
                <>
                  {fields.map((field, index) => (
                    <div
                      key={field.key}
                      className="border border-gray-300 rounded-lg p-6 mt-6"
                    >
                      <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-semibold">
                          {index === 0
                            ? "Tax Resident Information"
                            : `Additional Tax Resident Information ${index}`}
                        </h3>

                        {index !== 0 && (
                          <Button
                            type="link"
                            danger
                            onClick={() => remove(field.name)}
                          >
                            Remove
                          </Button>
                        )}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Form.Item
                          label="Select Country"
                          name={[field.name, "taxDomicileCountryId"]}
                          rules={[
                            {
                              required: true,
                              message: "Please select country",
                            },
                          ]}
                        >
                          <Select
                            placeholder="Select country"
                            options={countries}
                          />
                        </Form.Item>

                        <Form.Item shouldUpdate>
                          {({ getFieldValue }) => {
                            const noTIN = getFieldValue([
                              "taxDomiciles",
                              index,
                              "isTINIDAvailable",
                            ]);

                            return (
                              <Form.Item
                                label="Tax Identification Number"
                                name={[field.name, "taxIDNumber"]}
                                rules={
                                  noTIN
                                    ? []
                                    : [{ required: true, message: "Enter TIN" }]
                                }
                                className="w-full"
                                disabled={noTIN || isDisabled}
                              >
                                <Input
                                  placeholder="Enter tax identification number"
                                  disabled={noTIN || isDisabled}
                                />
                              </Form.Item>
                            );
                          }}
                        </Form.Item>

                        <Form.Item
                          name={[field.name, "isTINIDAvailable"]}
                          valuePropName="checked"
                          className="md:col-span-2"
                        >
                          <Checkbox>Check if you don't have TIN?</Checkbox>
                        </Form.Item>

                        <Form.Item className="md:col-span-2" shouldUpdate>
                          {({ getFieldValue }) => {
                            const noTIN = !getFieldValue([
                              "taxDomiciles",
                              index,
                              "isTINIDAvailable",
                            ]);

                            if (noTIN) return null;

                            return (
                              <Form.Item
                                label="Please select a reason"
                                name={[field.name, "taxDomicileReasonID"]}
                                rules={[
                                  {
                                    required: true,
                                    message: "Please select reason",
                                  },
                                ]}
                              >
                                <Radio.Group>
                                  {tinReasonOptions.map((r) => (
                                    <Radio
                                      key={r.value}
                                      value={r.value}
                                      className="block mb-2"
                                    >
                                      {r.label}
                                    </Radio>
                                  ))}
                                </Radio.Group>
                              </Form.Item>
                            );
                          }}
                        </Form.Item>

                        <AlternateTaxIdentifierField
                          field={field}
                          index={index}
                          label="ID or Passport Number"
                        />
                      </div>
                    </div>
                  ))}

                  {hasOtherCrsTaxResident && (
                    <div className="mt-6">
                      <Button
                        type="dashed"
                        block
                        onClick={() =>
                          add({
                            taxDomicileCountryId: null,
                            taxIDNumber: "",
                            isTINIDAvailable: false,
                            taxDomicileReasonID: null,
                            alternateTaxIdentifier: "",
                          })
                        }
                      >
                        + Add Another Tax Resident Country
                      </Button>
                    </div>
                  )}
                </>
              )}
            </Form.List>
          </div>
        </div>
      </div>

      {/* Politically Exposed Person Section */}
      <div className="mt-6 pt-6 border-t border-gray-200">
        <h3 className="text-lg font-semibold mb-4 text-primary">
          Do you qualify as Politically Exposed Person (PEP)? *
        </h3>

        <div className="mb-4">
          <p className="text-sm text-gray-600 mb-2">
            A Politically Exposed person is an individual who is or has been
            entrusted with a prominent public function or a family member/Close
            associate who is related to a PEP.
          </p>
        </div>

        <Form.Item
          name="isPEP"
          rules={[{ required: true, message: "Please select an option" }]}
        >
          <Radio.Group onChange={handlePEPChange}>
            <Radio value={true}>Yes</Radio>
            <Radio value={false}>No</Radio>
          </Radio.Group>
        </Form.Item>

        {isPEP && (
          <Form.Item
            label="Please provide details"
            name="pepDetails"
            rules={[
              { required: true, message: "Please provide PEP details" },
              { min: 7, message: "Minimum 7 characters long" },
            ]}
            className="mt-4"
          >
            <TextArea
              rows={4}
              placeholder="Please provide details about being a Politically Exposed Person"
            />
          </Form.Item>
        )}
      </div>

      <div className="mt-6 pt-6 border-t border-gray-200">
        <h3 className="text-lg font-semibold mb-4">Upload Documents</h3>

        <p className="text-sm text-gray-600 mb-4">
          Get verified your Identity and Proof of Residence documents to
          activate your account.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <Form.Item
            label="Document Type"
            name="proofType"
            rules={
              uploadedDocuments.length > 0
                ? []
                : [{ required: true, message: "Please select proof type" }]
            }
          >
            <Select
              placeholder="Select Document"
              options={proofTypeOptions}
              onChange={handleProofTypeChange}
            />
          </Form.Item>

          <Form.Item
            label="Document Sub Type"
            name="documentSubType"
            rules={
              uploadedDocuments.length > 0
                ? []
                : [
                    {
                      required: true,
                      message: "Please select document sub type",
                    },
                  ]
            }
          >
            <Select
              placeholder="Select Document"
              options={
                proofTypeOptions.find((p) => p.value === proofType)
                  ?.subTypeOptions || []
              }
              onChange={handleDocumentSubTypeChange}
              disabled={isDisabled || !proofType}
            />
          </Form.Item>
        </div>

        <p className="text-sm text-red-600 mb-4">
          File Types: .jpg, .pdf, .png, .doc and .docx; max file size 12 Mb.
        </p>

        <Form.Item
          name="document"
          rules={
            uploadedDocuments.length > 0
              ? []
              : [{ required: true, message: "Please upload document" }]
          }
        >
          <Dragger
            accept=".jpg,.jpeg,.png,.pdf,.doc,.docx"
            showUploadList={false}
            beforeUpload={() => false}
            fileList={documentFileList}
            onChange={handleDocumentChange}
            disabled={isDisabled || !proofType || !documentSubType}
          >
            <div className="w-full h-full">
              {!previewUrl ? (
                <div
                  onDragOver={(e) =>
                    documentFileList.length > 0 && e.preventDefault()
                  }
                  onDrop={(e) =>
                    documentFileList.length > 0 && e.preventDefault()
                  }
                >
                  {DocumentUploadButton}
                </div>
              ) : (
                <>
                  <img
                    src={previewUrl}
                    alt="preview"
                    className="h-[200px] w-full object-contain rounded"
                  />
                </>
              )}
            </div>
          </Dragger>
          {previewUrl && !isDisabled && (
            <span className="flex ml-4 h-12 gap-2">
              <button
                type="button"
                onClick={handleAddDocument}
                className="mt-3 bg-primary text-white px-4 py-2 rounded"
              >
                Add
              </button>
              <button
                type="button"
                onClick={handleRemoveDocument}
                className="mt-3 bg-red-500 text-white px-4 py-2 rounded"
              >
                Reset
              </button>
            </span>
          )}
        </Form.Item>

        <div className="mt-6">
          <TableWithSkeleton
            columns={documentColumns}
            data={uploadedDocuments}
            loading={false}
            rowKey="id"
          />
        </div>
      </div>
    </>
  );
};

export default IndividualBeneficiaryForm;
